import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { NavLink, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { api, type User } from './lib/api.ts';
import Login from './pages/Login.tsx';
import Register from './pages/Register.tsx';
import Pulpit from './pages/Pulpit.tsx';
import Praca from './pages/Praca.tsx';
import MyDay from './pages/MyDay.tsx';
import Pracownicy from './pages/Pracownicy.tsx';
import Historia from './pages/Historia.tsx';
import Reports from './pages/Reports.tsx';
import Orders from './pages/Orders.tsx';
import Admin from './pages/Admin.tsx';

// ---------------------------------------------------------------- sesja

interface Auth {
  user: User | null;
  setUser: (u: User | null) => void;
  logout: () => Promise<void>;
}
const AuthCtx = createContext<Auth>({ user: null, setUser: () => {}, logout: async () => {} });
export const useAuth = () => useContext(AuthCtx);

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    api.get<{ user: User }>('/auth/me')
      .then((r) => setUser(r.user))
      .catch(() => setUser(null))
      .finally(() => setReady(true));
  }, []);

  const logout = async () => {
    await api.post('/auth/logout').catch(() => {});
    setUser(null);
  };

  if (!ready) {
    return (
      <div className="min-h-dvh grid place-items-center bg-canvas">
        <p className="text-body-sm text-plum-muted" role="status">Wczytywanie…</p>
      </div>
    );
  }

  return (
    <AuthCtx.Provider value={{ user, setUser, logout }}>
      {user ? <Shell /> : <Login />}
    </AuthCtx.Provider>
  );
}

// ---------------------------------------------------------------- rama

/**
 * Nawigacja zależy od roli i nie obiecuje tego, czego nie da.
 * Pracownik nie widzi zakładek zakładowych, bo serwer i tak by je odrzucił —
 * pokazywanie ich było wcześniej pustą obietnicą.
 */
function Shell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const kierownik = user?.role !== 'worker';

  const zakladki: Array<[string, string]> = kierownik
    ? [
        ['/', 'Pulpit'],
        ['/praca', 'Praca'],
        ['/zlecenia', 'Zlecenia'],
        ['/pracownicy', 'Pracownicy'],
        ['/historia', 'Historia'],
        ['/analityka', 'Analityka'],
      ]
    : [
        ['/', 'Rejestracja'],
        ['/dzien', 'Mój dzień'],
        ['/historia', 'Historia'],
        ['/zlecenia', 'Zlecenia'],
      ];

  return (
    <div className="min-h-dvh flex flex-col bg-canvas">
      <header className="sticky top-0 z-30 bg-white border-b border-line">
        <div className="mx-auto max-w-content px-5 h-14 flex items-center gap-4">
          <span className="font-bold text-plum tracking-[-0.02em]">Efektywność A4</span>

          <nav className="hidden md:flex items-center gap-1 ml-4" aria-label="Główna">
            {zakladki.map(([to, tekst]) => (
              <NavLink key={to} to={to} end={to === '/'}
                       className={({ isActive }) => `nav-link ${isActive ? 'nav-link-on' : ''}`}>
                {tekst}
              </NavLink>
            ))}
            {user?.role === 'admin' && (
              <NavLink to="/panel" className={({ isActive }) => `nav-link ${isActive ? 'nav-link-on' : ''}`}>
                Panel
              </NavLink>
            )}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <span className="badge-neutral tnum">{user?.login}</span>
            <button className="btn-quiet" onClick={async () => { await logout(); navigate('/'); }}>
              Wyloguj
            </button>
          </div>
        </div>

        {/* Na wąskim ekranie nawigacja schodzi do drugiego rzędu, przewijalnego. */}
        <nav className="md:hidden mx-auto max-w-content px-3 pb-2 flex gap-1 overflow-x-auto" aria-label="Główna">
          {zakladki.map(([to, tekst]) => (
            <NavLink key={to} to={to} end={to === '/'}
                     className={({ isActive }) => `nav-link whitespace-nowrap ${isActive ? 'nav-link-on' : ''}`}>
              {tekst}
            </NavLink>
          ))}
          {user?.role === 'admin' && (
            <NavLink to="/panel" className={({ isActive }) => `nav-link whitespace-nowrap ${isActive ? 'nav-link-on' : ''}`}>
              Panel
            </NavLink>
          )}
        </nav>
      </header>

      <main className="flex-1 mx-auto w-full max-w-content px-5 py-6">
        <Routes>
          <Route path="/" element={kierownik ? <Pulpit /> : <Register />} />
          <Route path="/rejestracja" element={<Register />} />
          <Route path="/praca" element={<Praca />} />
          <Route path="/dzien" element={<MyDay />} />
          <Route path="/zlecenia" element={<Orders />} />
          <Route path="/pracownicy" element={<Pracownicy />} />
          <Route path="/pracownicy/:id" element={<Pracownicy />} />
          <Route path="/historia" element={<Historia />} />
          <Route path="/analityka" element={kierownik ? <Reports /> : <Navigate to="/" replace />} />
          <Route path="/panel" element={user?.role === 'admin' ? <Admin /> : <Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

export type { ReactNode };
